<template>
  <v-card class="mb-4" >

    <!-- Content -->
    <v-card-text class="pt-2" :class="getSaleClass(sale)">
      <v-row>
        <v-col cols="1" md="1">
        <v-checkbox
          :model-value="selected"
          @update:model-value="$emit('selection-changed', sale.ID)"
          hide-details
          density="compact"
        ></v-checkbox>
        </v-col>
        <v-col cols="12" md="2">
            <span class="text-caption text-medium-emphasis">
                {{ formatDate(sale.CreatedDate) }}
            </span>
        </v-col>
        <v-col cols="12" md="2">
            <a
          :href="`/sales/${sale.ID}`"
          color="primary"
          class="mr-2 text-body-1"
        >
          {{ sale.ID }}
        </a>
        </v-col>
        <v-col cols="12" md="2">
            <span class="ml-1">{{ sale.PONumber }}</span>
        </v-col>
        <v-col cols="12" md="2">
            <span class="ml-1">{{ sale.itemnumber }}</span>
        </v-col>
        <v-col cols="12" md="1">
            <span class="ml-1">${{ Number(sale.ebayprice).toFixed(2) }}</span>
        </v-col>
        <v-col cols="12" md="1">
            <span class="ml-1" v-if="sale.taxed">${{ taxes }}</span>
        </v-col>
        <v-col cols="12" md="1">
            <span :class="profitClass">${{ profit }}</span>
        </v-col>
</v-row>
<v-row>
        <v-col cols="1" md="1">
          <v-icon v-if="sale.taxed" color="primary" title="Taxed">mdi-currency-usd</v-icon>
          <v-icon v-if="sale.international" color="primary" title="International">mdi-airplane</v-icon>
          <v-icon v-if="sale.combined" color="primary" title="Combined">mdi-tag-multiple</v-icon>
        </v-col>
        <v-col cols="12" md="2">
            <span class="text-caption text-medium-emphasis">Buyer Id:</span>
              <span class="ml-1">{{ sale.BuyerID }}</span>
        </v-col>
        <v-col cols="12" md="3">
              <span class="text-caption text-medium-emphasis">Buyer name:</span>
              <span class="ml-1">{{ sale.shippingname }}</span>

        </v-col>

        <!-- Profit Details -->
        <v-col cols="12" md="2">
            <editable-field
              v-model="saleData.ActualMyCost"
              label="Cost"
              type="number"
              is-currency
              @edit-complete="handleUpdate('ActualMyCost', saleData.ActualMyCost)"
            />
        </v-col>
        <v-col cols="12" md="2">
            <editable-field
              v-model="saleData.ActualShipping"
              label="Shipping"
              type="number"
              is-currency
              @edit-complete="handleUpdate('ActualShipping', $event)"
            />
        </v-col>
        <v-col cols="12" v-if="marketplace == 'Amazon'" md="2">
            <editable-field
              v-model="saleData.amazonfees"
              label="Amazon Fees"
              type="number"
              is-currency
              @edit-complete="handleUpdate('amazonfees', $event)"
            />
        </v-col>
        <v-col cols="12" v-if="marketplace == 'eBay'" md="2">
            <editable-field
              v-model="saleData.ebayfees"
              label="eBay Fees"
              type="number"
              is-currency
              @edit-complete="handleUpdate('ebayfees', $event)"
            />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="1" md="1"></v-col>
        <v-col cols="11" md="5">
            <editable-field
              v-model="saleData.Note"
              label="Note"
              @edit-complete="handleUpdate('Note', $event)"
            />
        </v-col>
        <v-col cols="12" md="6" class="d-flex justify-end align-center">
          <div 
            v-if="sale.submitted"
          >
            {{ sale.submitted }} {{ sale.submitted_by ? 'by ' + sale.submitted_by : '' }}
            </div>
        <v-btn
          v-if="sale.submitted"
          color="secondary"
          class="ml-2"
          :loading="loading"
          @click="handleUpdate('submitted', '')"
        >
          Unsubmit
        </v-btn>
        <v-btn
          v-else
          color="success"
          class="ml-2"
          :loading="loading"
          @click="handleUpdate('submitted', 'Manual')"
        >
          Submit
        </v-btn>
        <v-btn
          :to="`/returns/initiate/${sale.ID}`"
          v-if="sale.submitted"
          class="ml-2"
          color="warning"
        >
          Return
        </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, defineProps, defineEmits, computed } from 'vue'
import EditableField from '@/components/common/EditableField.vue'
import { getSaleClass } from '@/utils/SaleClass'
import { useStoresStore } from '@/stores/storesStore'
import { formatDate } from '@/utils/helpers'

const storesStore = useStoresStore()

const props = defineProps({
  sale: {
    type: Object,
    required: true
  },
  selected: {
    type: Boolean,
    default: false
  }
})

const marketplace = computed(() => storesStore.getStoreBySlug(props.sale.Site)?.marketplace)

const saleData = ref({ ...props.sale })

const emit = defineEmits(['selection-changed', 'update:sale'])


const totalFees = computed(() => {
  const amazonFees = Number(props.sale.amazonfees || 0)
  const ebayFees = Number(props.sale.ebayfees || 0)
  return (amazonFees + ebayFees).toFixed(2)
})

const profit = computed(() => {
  const price = Number(props.sale.ebayprice || 0)
  const cost = Number(props.sale.ActualMyCost || 0)
  const shipping = Number(props.sale.ActualShipping || 0)
  const fees = Number(totalFees.value)
  return (price - cost - shipping - fees).toFixed(2)
})

const taxes = computed(() => {
  if (marketplace.value == 'eBay') {
    return Number(props.sale.ebaytaxes || 0).toFixed(2)
  } else if (marketplace.value == 'Amazon') {
    return Number((props.sale.ebayprice || 0)  * 8.25 / 100).toFixed(2)
  }
  return 0
})

const profitClass = computed(() => {
  const profitValue = Number(profit.value)
  return {
    'ml-1': true,
    'text-success': profitValue > 0,
    'text-error': profitValue < 0
  }
})

// Update handlers
const handleUpdate = async (field, value) => {
  try {
    const updatedSale = { ...props.sale }
    updatedSale[field] = value
    emit('update:sale', updatedSale)
  } catch (error) {
    console.error(`Error updating ${field}:`, error)
  }
}

</script> 